import {
	Box,
	Grid,
	MenuItem,
	Select,
	SelectChangeEvent,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { MutableRefObject, useCallback, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import useAPIFetch from './useAPIFetch'
import { Criteria } from '@components/customized/datatable'
import CustomizedSearch from '@components/customized/search'
import { FiltersPopover } from '@components/customized/datatable/filtersPopover'
import SingleValueSearch from '@components/customized/SingleValueSearch'

export interface Sorting {
	by: string
	order_by: 'ASC' | 'DESC'
}

export interface FilterCriteria {
	target: string
	operator: string
	value: any[]
}

export type useTableViewProps<T> = {
	columns: {
		key: string
		desc: string
		hidden?: boolean
		renderCell?: (
			record: T,
			navigate?: NavigateFunction,
			columnIndex?: number,
			rowIndex?: number,
		) => JSX.Element
		width?: string | number
		disableSort?: boolean
		disableFilter?: boolean
	}[]
	tableHeight?: number | string
	rowsPerPage?: number
	reloadCallback: (
		page?: number,
		page_size?: number,
		filter?: any,
		search?: string | (string | never[])[] | null,
		sorting?: Sorting,
		cancelToken?: any,
	) => Promise<{ data: T[]; total_count: number; criterion: any } | undefined>
	onRowClick?: (navigate: NavigateFunction, record: T) => void
	toolbar?: boolean
	customRow?: boolean
	mountedRef: MutableRefObject<boolean>
	additionButton?: JSX.Element
	hiddenHeader?: boolean
	criterion?: Criteria[]
	haveGlobalSearch?: boolean
	dropboxSelection?: Criteria
	defaultFilterCriteria?: any
	renderNoResult?: boolean
}

const CenterFunctionBar = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	gap: '8px',
	marginBottom: '8px',
	paddingRight: '8px',
	paddingLeft: '8px',
	justifyContent: 'center'
}))

const EndFunctionBar = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	gap: '8px',
	marginBottom: '8px',
	paddingRight: '8px',
	paddingLeft: '8px',
	justifyContent: 'flex-end'
}))

const useTableView = function <T>(
	props: useTableViewProps<T>,
): [TableView: () => JSX.Element, reload: () => void] {
	const { t } = useTranslation()
	const { setRequest, isLoading } = useAPIFetch()
	const [list, setList] = useState<T[] | undefined>()

	const [totalCnt, setTotalCnt] = useState<number>(100)
	const navigate = useNavigate()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage || 25)
	const [sorting, setSorting] = useState<Sorting | undefined>(undefined)
	const [globalSearch, setGlobalSearch] = useState<(string | never[])[]>([])
	const [filter, setFilter] = useState<any>(props.defaultFilterCriteria ?? {})
	const [newCriterion, setNewCriterion] = useState<any>(props.criterion ?? {})

	const theme = useTheme()
	const downMd = useMediaQuery(theme.breakpoints.down('md'))

	const reloadCallback = useCallback(async () => {
		reload(page, rowsPerPage, filter)
	}, [props.reloadCallback])

	const isLastPageEmpty = (rowsPerPage * (page+1) - totalCnt) === rowsPerPage

	useEffect(()=>{
		if(isLastPageEmpty && totalCnt){
			setPage(page-1)
			reload(page-1, rowsPerPage, filter)
		}
	}, [isLastPageEmpty])

	const reload = (
		currentPage: number,
		currentPageSize: number,
		filter?: any,
		search?: string | (string | never[])[] | null,
		sorting?: Sorting,
		resetPage?: boolean,
	) => {
		if (!isLoading) {
			if (resetPage) {
				setPage(0)
				currentPage = 0
			}

			setRequest({
				callback: async (cancelToken: any) => {
					await props.reloadCallback(currentPage + 1, currentPageSize, filter, search, sorting).then((x) => {
						if (props.mountedRef.current) {
							setList(x?.data)
							setTotalCnt(x?.total_count || 0)
							if( x?.criterion !== undefined && x?.criterion !== null && Object.keys(x?.criterion).length !== 0 ) setNewCriterion(x?.criterion)
						}
					})
				},
			})
		}
	}


	const handleOnSort = (by: string) => {
		const order_by = by !== sorting?.by ? 'ASC' : sorting?.order_by !== 'ASC' ? 'ASC' : 'DESC'
		const _sorting: Sorting = { by: by, order_by: order_by }
		setSorting(_sorting)
		reload(0, rowsPerPage, filter, globalSearch, _sorting)
	}

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage)
		reload(newPage, rowsPerPage, filter, globalSearch, sorting)
	}

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value)
		setPage(0)
		reload(0, +event.target.value, filter, globalSearch, sorting)
	}

	const onSubmitFilter = (filter: any) => {
		console.log('filter', filter)
		reload(0, rowsPerPage, filter, globalSearch, sorting)
	}

	const TableView = () => {
		return (
			<>
				<CenterFunctionBar>
					{props.dropboxSelection && (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-around',
								alignItems: 'center',
								width: '65%',
							}}
						>
							<Typography variant="h6">{props.dropboxSelection?.name}</Typography>
							<Select
								value={filter[props.dropboxSelection?.key]?.value}
								onChange={(event) => {
									const _filter =  filter
									_filter[props.dropboxSelection?.key ?? "temp"] = { operator: '=', value: [event.target.value] } 
									setFilter(_filter)
									reload(0, rowsPerPage, _filter ?? undefined )
								}}
								MenuProps={{
									anchorOrigin: {
										vertical: 'bottom',
										horizontal: 'left',
									},
									transformOrigin: {
										vertical: 'top',
										horizontal: 'left',
									},
									keepMounted: true,
								}}
								variant="standard"
								sx={{ width: '75%' }}
							>
								{props.dropboxSelection?.options?.map((elem: any, index: number) => {
									return (
										<MenuItem key={index} value={elem.key}>
											<Typography variant="body2">{elem.value}</Typography>
										</MenuItem>
									)
								})}
							</Select>
						</Box>
					)}
					{props.haveGlobalSearch && (
						<>
							<SingleValueSearch
								value={globalSearch}
								sx={{ width: props.dropboxSelection ? '30%' : '80%' }}
								handleOnSearch={(selectedValue) => {
									reload(0, rowsPerPage, filter ?? undefined, selectedValue)
									setGlobalSearch(selectedValue)
								}}
								setGlobalSearch={setGlobalSearch}
							/>
						</>
					)}
				</CenterFunctionBar>
				<EndFunctionBar>
				{props.additionButton}
					{props.toolbar && (
							<FiltersPopover
								criterion={newCriterion}
								onSubmit={onSubmitFilter}
								filter={filter}
								setFilter={setFilter}
							/>
					)}
				</EndFunctionBar>

				<TableContainer sx={{ height: props.tableHeight ?? '630px' }}>
					<Table stickyHeader aria-label="sticky table" size="small">
						{!props.hiddenHeader && (
							<TableHead>
								<TableRow key={'header'} sx={{ height: '25px' }}>
									{props.columns
										.filter((header) => !header.hidden)
										.map((column, columnIndex) => {
											return (
												<TableCell
													key={`header_${columnIndex}`}
													sx={{
														backgroundColor: 'white',
														minWidth: column.width ?? '100px',
													}}
													align="left"
													sortDirection={
														sorting && sorting.by === column.key && !column?.disableSort
															? sorting.order_by === 'ASC'
																? 'asc'
																: 'desc'
															: false
													}
												>
													{column.disableSort ? (
														column.desc
													) : (
														<TableSortLabel
															active={sorting && sorting.by === column.key}
															direction={
																sorting && sorting.by?.toLowerCase() === column.key
																	? sorting.order_by === 'ASC'
																		? 'asc'
																		: 'desc'
																	: undefined
															}
															onClick={() => handleOnSort(column.key)}
															disabled={column.disableSort}
														>
															{column.desc}
														</TableSortLabel>
													)}
													{/* {!column?.disableSort && !column?.disableFilter && <HeaderRowPopover />} */}
												</TableCell>
											)
										})}
								</TableRow>
							</TableHead>
						)}
						{isLoading ? (
							<TableBody>
								{[...Array(20)].map((e, i) => {
									return (
										<TableRow key={`skeleton_r_${i}`}>
											{props.columns.map((r, index) => {
												return (
													<TableCell key={`skeleton_${index}`}>
														<Skeleton />
													</TableCell>
												)
											})}
										</TableRow>
									)
								})}
							</TableBody>
						) : (
							<TableBody>
								{list?.map((record, rowIndex) => {
									return (
										<TableRow
											onClick={() => {
												//   dispatch({ type: 'clear' })
												if (props.onRowClick) props.onRowClick(navigate, record)
											}}
											key={`table-record-${rowIndex}`}
											sx={{
												'&:hover': {
													cursor: 'pointer',
													backgroundColor: '#cfcfcf',
												},
											}}
										>
											{props.columns
												.filter((header) => !header.hidden)
												.map((column, columnIndex) => (
													<TableCell
														key={`table-record-${rowIndex}-${columnIndex}`}
														sx={{ minWidth: column.width ?? '100px' }}
													>
														{column.renderCell
															? column.renderCell(record, navigate, columnIndex, rowIndex)
															: '-'}
													</TableCell>
												))}
										</TableRow>
									)
								})}
							</TableBody>
						)}
					</Table>
					{(props.renderNoResult && (totalCnt === 0)) && (
						<Box sx={{display: 'flex', 
								justifyContent: 'center',
								alignItems: 'center',
								height: '80%',
								color: '#AAAAAA',
								fontSize: '20px',
							}}>
							&nbsp;There are no search results.<br/> Please try another keywords.
						</Box>
					)}
				</TableContainer>
				<Grid container spacing={1}>
					<Grid item md={11}>
						<TablePagination
							component="div"
							count={totalCnt}
							rowsPerPage={rowsPerPage}
							rowsPerPageOptions={[]}
							page={page}
							SelectProps={{
								inputProps: {
									'aria-label': 'rows per page',
								},
								native: true,
							}}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Grid>
				</Grid>
			</>
		)
	}

	return [TableView, reloadCallback]
}

export default useTableView
